import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ProjectWorkbookUploadEventBus } from '../events/project-workbook-upload';
import { longPullUISelectors } from '../store';

interface WorkspaceContextType {
  isDocumentsGenerated: boolean;
  isGenerating: boolean;
  longPullItemId: number | null;
  setLongPullItemId: (id: number | null) => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export const useWorkspace = () => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used within a WorkspaceProvider');
  return ctx;
};

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [longPullItemId, setLongPullItemId] = useState<number | null>(null);

  const longPullQueue = useSelector((state: any) => longPullUISelectors.getQueueByKey(state, 'global'));

  const isDocumentsGenerated = useMemo(() => {
    if (!longPullItemId) return false;
    const currentItem = (longPullQueue || []).find((item) => item.id === longPullItemId);
    return !!(currentItem?.isCompleted && currentItem?.type === 'success');
  }, [longPullItemId, longPullQueue]);

  const isGenerating = useMemo(() => {
    if (!longPullItemId) return false;
    const currentItem = (longPullQueue || []).find((item) => item.id === longPullItemId);
    return !!currentItem && !currentItem.isCompleted;
  }, [longPullItemId, longPullQueue]);

  useEffect(() => {
    const unsubscribe = ProjectWorkbookUploadEventBus.subscribe(
      '@clatter/PROJECT_WORKBOOK_UPLOAD_COMPLETED',
      () => setLongPullItemId(null)
    );

    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <WorkspaceContext.Provider
      value={{
        longPullItemId,
        setLongPullItemId,
        isDocumentsGenerated,
        isGenerating,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};
